import { useEffect, useRef, useState } from 'react';
import { tokenStore } from '@/lib/api';
import type { WSMessage } from '@/lib/types';

type Listener = (msg: WSMessage) => void;

// Single shared WebSocket per page lifetime. Auto-reconnect on close.
// Pass token via query param (matches the existing server.js handshake).

class WSManager {
  private socket: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private reconnectTimer: number | null = null;
  private lastFrameAt = 0;

  connect() {
    const token = tokenStore.get();
    if (!token) return;
    if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) {
      return;
    }
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const url = `${proto}//${location.host}/ws?token=${encodeURIComponent(token)}`;
    const sock = new WebSocket(url);
    this.socket = sock;
    sock.onmessage = (e) => {
      this.lastFrameAt = Date.now();
      try {
        const msg: WSMessage = JSON.parse(e.data);
        this.listeners.forEach((fn) => fn(msg));
      } catch { /* malformed frame, ignore */ }
    };
    sock.onclose = () => {
      this.socket = null;
      if (tokenStore.get()) {
        if (this.reconnectTimer) window.clearTimeout(this.reconnectTimer);
        this.reconnectTimer = window.setTimeout(() => this.connect(), 3000);
      }
    };
  }

  disconnect() {
    if (this.reconnectTimer) { window.clearTimeout(this.reconnectTimer); this.reconnectTimer = null; }
    if (this.socket) {
      const s = this.socket;
      this.socket = null;
      try { s.close(); } catch { /* */ }
    }
  }

  subscribe(fn: Listener) {
    this.listeners.add(fn);
    return () => { this.listeners.delete(fn); };
  }

  getLastFrameAt() { return this.lastFrameAt; }
}

export const wsManager = new WSManager();

// React hook: ensures a connection exists while authenticated, and lets a
// component subscribe to WSMessage frames. Also exposes a "live" indicator
// — true when a frame was received in the last `freshMs` window.
export function useWebSocket(onMessage?: Listener, freshMs = 8000) {
  const [live, setLive] = useState(false);
  const onMsgRef = useRef(onMessage);
  onMsgRef.current = onMessage;

  useEffect(() => {
    wsManager.connect();
    const unsub = wsManager.subscribe((msg) => {
      onMsgRef.current?.(msg);
    });
    const tick = window.setInterval(() => {
      setLive(Date.now() - wsManager.getLastFrameAt() < freshMs);
    }, 1000);
    return () => {
      unsub();
      window.clearInterval(tick);
    };
  }, [freshMs]);

  return { live };
}
